import { createTheme } from '@mui/material/styles';
import {
  neutral,
  brand,
  accent,
  surface,
  border,
  radius,
  spacingUnit,
  shadow,
  motion,
  fontStack,
} from './tokens';

/**
 * The application theme — ROLLER SKO brand.
 *
 * All component styling should flow from here rather than from per-component
 * `sx` colour literals, so that a palette change is a one-file change.
 *
 * Two rules keep this looking deliberate rather than "MUI with a hex swap":
 *  1. Borders are translucent brand-tinted hairlines (see tokens.border), not
 *     opaque grey. On a navy canvas, opaque borders read as dirt.
 *  2. Elevation is a step in surface luminance (canvas → raised → overlay),
 *     with shadow reserved for genuinely floating layers (menus, dialogs).
 */
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brand[500], // ROLLER Blue #0052FF
      light: brand[400],
      dark: brand[600],
      contrastText: neutral[0],
    },
    secondary: {
      main: accent.sky,
      light: brand[300],
      dark: brand[700],
      contrastText: neutral[0],
    },
    error: { main: accent.red, light: '#FF6B63' }, // ROLLER Red
    warning: { main: accent.amber, light: '#FFBE4D' },
    info: { main: accent.sky, light: brand[300] },
    success: { main: accent.emerald, light: '#45D87F' },
    background: {
      default: surface.canvas, // ROLLER Navy
      paper: surface.raised,
    },
    text: {
      primary: '#EEF4FF',
      secondary: neutral[400],
      disabled: neutral[500],
    },
    divider: border.standard,
    action: {
      hover: 'rgba(200, 216, 255, 0.06)',
      selected: 'rgba(0, 82, 255, 0.18)',
      focus: 'rgba(0, 82, 255, 0.28)',
    },
  },

  shape: { borderRadius: radius.md },

  spacing: spacingUnit,

  typography: {
    fontFamily: fontStack,
    // Tightened tracking on headings reads as more deliberate/modern than
    // MUI's defaults, which are tuned for Roboto.
    h1: { fontWeight: 700, letterSpacing: '-0.03em', fontSize: '2.5rem' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em', fontSize: '2rem' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.75rem' },
    h4: { fontWeight: 650, letterSpacing: '-0.02em', fontSize: '1.5rem' },
    h5: { fontWeight: 650, letterSpacing: '-0.015em', fontSize: '1.25rem' },
    h6: { fontWeight: 650, letterSpacing: '-0.01em', fontSize: '1.0625rem' },
    subtitle1: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem', letterSpacing: 0 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: 0 },
    caption: { fontSize: '0.75rem', letterSpacing: '0.01em' },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: surface.canvas,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: 'rgba(200, 216, 255, 0.2)',
          borderRadius: radius.pill,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(200, 216, 255, 0.34)',
          backgroundClip: 'content-box',
        },
        // Keyboard focus must stay visible and on-brand.
        '*:focus-visible': {
          outline: `2px solid ${brand[400]}`,
          outlineOffset: 2,
        },
        // Respect users who prefer less motion.
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: border.standard },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: surface.raised,
          border: `1px solid ${border.standard}`,
          borderRadius: radius.lg,
          backgroundImage: 'none',
          transition: `border-color ${motion.base}, background-color ${motion.base}`,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacingUnit * 3,
          '&:last-child': { paddingBottom: spacingUnit * 3 },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: radius.md,
          padding: '8px 16px',
          transition: `background-color ${motion.fast}, border-color ${motion.fast}, box-shadow ${motion.fast}`,
          '&:focus-visible': { boxShadow: shadow.focus },
        },
        containedPrimary: {
          backgroundColor: brand[500],
          '&:hover': { backgroundColor: brand[600] },
        },
        outlined: {
          borderColor: border.strong,
          '&:hover': {
            borderColor: brand[400],
            backgroundColor: 'rgba(0, 82, 255, 0.08)',
          },
        },
        text: {
          '&:hover': { backgroundColor: 'rgba(200, 216, 255, 0.06)' },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: `background-color ${motion.fast}`,
          '&:focus-visible': { boxShadow: shadow.focus },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 600, fontSize: '0.75rem' },
        sizeSmall: { height: 22 },
        outlined: { borderColor: border.strong },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: 'rgba(200, 216, 255, 0.04)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: border.standard,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: border.strong,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand[500],
            borderWidth: 1,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: surface.overlay,
          border: `1px solid ${border.strong}`,
          borderRadius: radius.sm,
          fontSize: '0.75rem',
          padding: '6px 10px',
          boxShadow: shadow.md,
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: border.subtle },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: neutral[400],
          backgroundColor: 'rgba(200, 216, 255, 0.035)',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color ${motion.fast}`,
          '&:hover': { backgroundColor: 'rgba(200, 216, 255, 0.045)' },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.md, border: '1px solid transparent' },
        standardError: {
          backgroundColor: 'rgba(255, 59, 48, 0.12)',
          borderColor: 'rgba(255, 59, 48, 0.32)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(23, 201, 100, 0.12)',
          borderColor: 'rgba(23, 201, 100, 0.32)',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 165, 36, 0.12)',
          borderColor: 'rgba(245, 165, 36, 0.32)',
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 82, 255, 0.14)',
          borderColor: 'rgba(0, 82, 255, 0.34)',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          height: 6,
          backgroundColor: 'rgba(200, 216, 255, 0.1)',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.sunken,
          borderRight: `1px solid ${border.standard}`,
          backgroundImage: 'none',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          // Navy canvas at 82% with a blur, so content scrolls under it.
          backgroundColor: 'rgba(10, 17, 40, 0.82)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${border.standard}`,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.overlay,
          border: `1px solid ${border.standard}`,
          borderRadius: radius.md,
          boxShadow: shadow.lg,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.raised,
          border: `1px solid ${border.standard}`,
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
          backgroundImage: 'none',
        },
      },
    },
  },
});

export default theme;
